import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from '../utils/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ErrorsService {
  private static readonly EXPIRED_TOKEN_DETAIL =
    'El token dado no es valido para ningun tipo de token';

  /** SimpleJWT (and DRF) error codes that mean "session is gone". */
  private static readonly JWT_ERROR_CODES = [
    'token_not_valid',
    'token_expired',
    'invalid_token',
    'authentication_failed',
  ];

  /**
   * Substrings that identify a JWT/auth error inside any string field of the
   * response body. Conservative on purpose: we want shape-agnostic detection
   * (e.g. `{ message: 'token expired' }`) without false positives from form
   * errors that happen to mention "token" in passing.
   */
  private static readonly JWT_MESSAGE_MARKERS = [
    'token dado no es valido',   // es-ES (SimpleJWT default)
    'token donat no és vàlid',   // ca
    'given token not valid',     // en (SimpleJWT)
    'token is invalid',
    'token has expired',
    'token expired',
  ];

  private readonly errorMappings: Record<string, string> = {
    'No se encontró cuenta activa con las credenciales provistas':
      'services.error.messages.account-not-found',
    [ErrorsService.EXPIRED_TOKEN_DETAIL]:
      'services.error.messages.expired-session-message',
    'Introduzca una dirección de correo electrónico válida.':
      'services.error.messages.email-invalid',
    'Este campo es requerido.': 'services.error.messages.field-required',
  };

  constructor(
    private toastService: ToastService,
    private cookieService: CookieService,
    private translate: TranslateService
  ) { }

  handleErrors(errors: any): void {
    if (this.isExpiredSession(errors)) {
      this.handleExpiredSession();
      return;
    }

    const body = errors?.error ?? errors ?? {};
    const flattenedMsgs = this.flattenErrorMessages(body);
    const translatedErrors = flattenedMsgs.map(
      (msg) => `<li>${this.translate.instant(this.errorMappings[msg] || msg)}</li>`
    );

    const errorListHTML =
      translatedErrors.length <= 5
        ? translatedErrors.join('')
        : `<li>${this.translate.instant('services.error.too-many-errors')}</li>`;

    const htmlErrorsContent = `<ul class="list-dots">${errorListHTML}</ul>`;

    this.toastService.showNotificationModal(
      this.translate.instant('services.error.modal-title'),
      htmlErrorsContent,
      'notification',
      this.translate.instant('services.error.modal-button-label')
    );
  }

  /**
   * Detects a JWT/auth error across the shapes we may receive:
   *  - HttpErrorResponse with status 401 (canonical signal).
   *  - SimpleJWT body `{ detail, code: 'token_not_valid', ... }`.
   *  - Alternative shapes like `{ message: 'token expired' }` or
   *    `{ errors: ['token has expired'] }`, matched against a curated marker
   *    list to avoid false positives from form errors that mention "token".
   */
  private isExpiredSession(errors: any): boolean {
    if (errors?.status === 401) return true;

    const body = errors?.error ?? errors;
    if (!body || typeof body !== 'object') return false;

    if (
      typeof body.code === 'string' &&
      ErrorsService.JWT_ERROR_CODES.includes(body.code.toLowerCase())
    ) {
      return true;
    }

    for (const value of Object.values(body)) {
      const strings: string[] =
        typeof value === 'string'
          ? [value]
          : Array.isArray(value)
          ? value.filter((v): v is string => typeof v === 'string')
          : [];
      for (const text of strings) {
        const lower = text.toLowerCase();
        if (ErrorsService.JWT_MESSAGE_MARKERS.some((m) => lower.includes(m))) {
          return true;
        }
      }
    }

    return false;
  }

  private handleExpiredSession(): void {
    this.cookieService.delete('gr_access');
    this.toastService.showNotificationModal(
      this.translate.instant('services.error.expired-session-title'),
      this.translate.instant('services.error.expired-session-message'),
      'notification',
      this.translate.instant('services.error.expired-token-modal-message'),
      '/signin'
    );
  }

  private flattenErrorMessages(body: any): string[] {
    const values = Object.values(body ?? {});
    return values.flat().map((msg) => String(msg));
  }
}
